// Bond Details tab in side-pane

import type { LiveInventoryPostResponse } from '@/types/api/live-inventory'

import { useMemo } from 'react'

import Accordion, { AccordionType } from '@/components/atomic/accordion'
import SummaryBannerGrid from '@/components/atomic/summary-banner-grid'
import { BodySubtext, BodyXS } from '@/components/atomic/text'
import { useGetRatingsAccess } from '@/hooks/use-rating-columns'
import { useUserRiskGroupId } from '@/hooks/use-user-risk-group-id'
import { useGetLiveInventoryPostQuery } from '@/legacy-api/data-slice'

import {
  formatFieldValue,
  getAllReferenceDataFields,
  getBondSummaryItems,
  getFieldLabel,
  referenceDataFields,
} from './helpers'
import { SummaryBannerContainer } from './styles'

function AccordionLabel({ label }: { label: string }) {
  return <BodySubtext>{label}</BodySubtext>
}

function AccordionItem({
  label,
  value,
  isFirst = false,
}: {
  label: string
  value: string
  isFirst?: boolean
}) {
  return (
    <div
      className={`py-3 flex justify-between ${
        !isFirst ? 'border-t border-primary-border' : ''
      }`}
    >
      <BodyXS className="text-secondary">{label}</BodyXS>
      <BodyXS>{value}</BodyXS>
    </div>
  )
}

// create accordion content based on category
function createAccordionContent(
  category: keyof typeof referenceDataFields,
  data: LiveInventoryPostResponse['data'][0] | null,
) {
  if (!data) return null

  return (
    <div className="space-y-2">
      {referenceDataFields[category].map((field, index) => (
        <div key={field}>
          <AccordionItem
            label={getFieldLabel(field)}
            value={formatFieldValue(
              field,
              data[field as keyof LiveInventoryPostResponse['data'][0]],
            )}
            isFirst={index === 0}
          />
        </div>
      ))}
    </div>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ProposalBondDetails({ isin }: { isin: string }) {
  const { riskGroupId } = useUserRiskGroupId()
  const { hasSPAccess, hasMoodysAccess } = useGetRatingsAccess()

  const { data: referenceData } = useGetLiveInventoryPostQuery({
    filters: {
      isin: { equal_to: isin },
    },
    returns: getAllReferenceDataFields(),
    risk_group_id: riskGroupId,
  })

  const bondData = referenceData?.data[0]

  const summaryItems = useMemo(() => {
    return getBondSummaryItems(bondData ?? null)
  }, [bondData])

  const accordionItems = useMemo(() => {
    const items = [
      {
        key: 'issue-details',
        label: <AccordionLabel label="Issue details" />,
        value: 'issue-details',
        content: createAccordionContent('issueDetails', bondData ?? null),
        className: 'px-4',
      },
      {
        key: 'redemption-details',
        label: <AccordionLabel label="Redemption details" />,
        value: 'redemption-details',
        content: createAccordionContent('redemptionDetails', bondData ?? null),
        className: 'px-4',
      },
      {
        key: 'country-information',
        label: <AccordionLabel label="Country information" />,
        value: 'country-information',
        content: createAccordionContent('countryInformation', bondData ?? null),
        className: 'px-4',
      },
      {
        key: 'dates-eligibility',
        label: <AccordionLabel label="Dates & eligibility" />,
        value: 'dates-eligibility',
        content: createAccordionContent(
          'datesAndEligibility',
          bondData ?? null,
        ),
        className: 'px-4',
      },
    ]

    // Add Moody's ratings if accessible
    if (hasMoodysAccess) {
      items.push({
        key: 'moodys-ratings',
        label: <AccordionLabel label="Moody's ratings" />,
        value: 'moodys-ratings',
        content: createAccordionContent('moodysRatings', bondData ?? null),
        className: 'px-4',
      })
    }

    // Add S&P ratings if accessible
    if (hasSPAccess) {
      items.push({
        key: 'sp-ratings',
        label: <AccordionLabel label="S&P ratings" />,
        value: 'sp-ratings',
        content: createAccordionContent('spRatings', bondData ?? null),
        className: 'px-4',
      })
    }

    items.push({
      key: 'pricing-analytics',
      label: <AccordionLabel label="Pricing & analytics" />,
      value: 'pricing-analytics',
      content: createAccordionContent('pricingAndAnalytics', bondData ?? null),
      className: 'px-4 border-0',
    })

    return items
  }, [bondData, hasMoodysAccess, hasSPAccess])

  return (
    <>
      <SummaryBannerContainer>
        <SummaryBannerGrid summaryItems={summaryItems} columns={4} />
      </SummaryBannerContainer>

      <Accordion
        items={accordionItems}
        type={AccordionType.Single}
        collapsible
      />
    </>
  )
}
